import { useTranslation } from "react-i18next";
import { Box, Chip, Skeleton, Typography } from "@mui/material";

import { Service } from "../../../types/service.type";

import ServiceDetails from "./ServiceDetails";

const ServiceBanner = ({ banner, isLoading }: { banner?: { url: string }; isLoading?: boolean }) => {
  if (isLoading) return <Skeleton variant="rectangular" width="100%" height={260} />;

  return (
    <Box
      sx={{
        ...styles.banner,
        backgroundImage: banner ? `url(${banner.url})` : "none",
        backgroundColor: "#F5F5F5",
      }}
    />
  );
};

const DescriptionSection = ({ description, isLoading }: { description?: string; isLoading?: boolean }) => {
  const { t } = useTranslation("goService");

  if (isLoading)
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <Skeleton variant="text" width={120} height={28} />
        <Skeleton variant="text" width="100%" height={20} />
        <Skeleton variant="text" width="90%" height={20} />
        <Skeleton variant="text" width="95%" height={20} />
      </Box>
    );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <Typography sx={styles.sectionTitle}>{t("servicePage.description")}</Typography>
      <Typography sx={styles.description}>{description}</Typography>
    </Box>
  );
};

const CategorySection = ({
  category,
  subCategory,
  isLoading,
}: {
  category?: string;
  subCategory?: string;
  isLoading?: boolean;
}) => {
  const { t } = useTranslation("goService");

  if (isLoading)
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <Skeleton variant="text" width={100} height={28} />
        <Box sx={{ display: "flex", gap: "8px" }}>
          <Skeleton variant="rounded" width={120} height={28} />
          <Skeleton variant="rounded" width={140} height={28} />
        </Box>
      </Box>
    );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <Typography sx={styles.sectionTitle}>{t("servicePage.category")}</Typography>
      <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        {category && <Chip sx={styles.categoryChip} label={t(`serviceCategory.${category}`)} />}
        {subCategory && <Chip sx={styles.categoryChip} label={t(`serviceSubCategory.${subCategory}`)} />}
      </Box>
    </Box>
  );
};

const ServiceDescription = ({ service, isServiceLoading }: { service?: Service; isServiceLoading: boolean }) => {
  return (
    <Box sx={styles.container}>
      <ServiceBanner banner={service?.banner} isLoading={isServiceLoading} />
      <Box sx={styles.contentWrapper}>
        <DescriptionSection description={service?.description} isLoading={isServiceLoading} />
        <CategorySection category={service?.category} subCategory={service?.subCategory} isLoading={isServiceLoading} />
        <ServiceDetails service={service} isLoading={isServiceLoading} />
      </Box>
    </Box>
  );
};

export default ServiceDescription;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    borderRadius: "8px",
    overflow: "hidden",
    border: "1px solid #EAECF0",
    background: "var(--White, #FFF)",
  },
  banner: {
    width: "100%",
    height: "260px",
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  contentWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    padding: "0 16px 24px",
  },
  sectionTitle: {
    color: "var(--Gray-900, #1D2026)",
    fontFamily: "Montserrat",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
  },
  description: {
    fontFamily: "Montserrat",
    color: "var(--Gray-700, #4E5566)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "150%",
  },
  categoryChip: {
    color: "var(--Green---100, #008B07)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
    padding: "4px 12px",
    borderRadius: "16px",
    background: "var(--Green---25, #E7EFED)",
    "& .MuiChip-label": {
      padding: "0",
    },
  },
} as const;
